import { getDefaultStore } from 'jotai';

import { chronicleSessionIdAtom } from '@/atoms/chronicle';
import { offlineCaptureRunningAtom } from '@/atoms/offline-recording/sync';
import { isDesktopRecordingActive } from '@/hooks/desktop/isDesktopRecordingActive';
import { isRecordingInProgress } from '@/hooks/desktop/useChronicleSessionControl';
import { isTauri } from '@/tauri/utils';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { activeSessionIdAtom } from '@heidi/session/data';

/**
 * Is a recording active right now, from every layer that can hold one? The
 * app-side binding of the switch guard's `isRecordingActive` port.
 *
 * The two atoms are the cheap, platform-neutral signals (an online session
 * recording; an offline capture that is not paused). Below them, desktop asks
 * `isDesktopRecordingActive`, which adds the Rust-side probes because native
 * capture can outlive the FE state; web asks the FE layers alone (the Redux
 * timer, the Chronicle session, the cached `recording_status`).
 */
export async function isRecordingActiveForWorkspaceSwitch(): Promise<boolean> {
  const store = getDefaultStore();
  if (store.get(activeSessionIdAtom) !== null || store.get(offlineCaptureRunningAtom)) {
    return true;
  }

  const probe = {
    sessionId: getCurrentSessionId() ?? '',
    chronicleSessionId: store.get(chronicleSessionIdAtom),
    queryClient: reactQueryClient,
  };

  if (isTauri()) {
    return isDesktopRecordingActive(probe);
  }
  return isRecordingInProgress(probe).inProgress;
}
